/**
 * Shared role dropdown options for Invite User and Edit Role modals.
 *
 * Agency (teamContext=agency): Owner → Admin → Viewer → Traffic Viewer
 * LYT (teamContext=lyt): Admin → User (lyt_admin → lyt_employee)
 */
import { formatRole, formatLytTeamRole } from '../../utils/role-utils';
import {
    ROLE_NAMES,
    LEGACY_ROLE_VALUES,
    AGENCY_TYPES,
    LYT_ROLE_DISPLAY_ORDER,
    getAgencyRoleDisplayOrder,
    getAgencyOwnerAdminAssignableRoleNames,
    getAgencyViewerAssignableRoleNames,
    ALL_ASSIGNABLE_ROLE_DISPLAY_ORDER,
    sortRolesByDisplayOrder,
} from '../../constants/roles.constants';

export const TEAM_CONTEXT = {
    AGENCY: 'agency',
    LYT: 'lyt',
};

const API_ROLES = ROLE_NAMES;
const LEGACY_ROLES = LEGACY_ROLE_VALUES;

const pickRoles = (userRolesList, orderedNames) =>
    sortRolesByDisplayOrder(
        userRolesList.filter((role) => orderedNames.includes(role.role_name)),
        orderedNames
    );

/** Dropdown label for a role option (agency vs LYT team wording). */
export const formatRoleOptionLabel = (roleName, teamContext) =>
    (teamContext === TEAM_CONTEXT.LYT ? formatLytTeamRole(roleName) : formatRole(roleName));

/** Subtext descriptions shown below role labels in the dropdown. */
const ROLE_SUBTEXTS = {
    [API_ROLES.TRANSIT_AGENCY_OWNER]: 'Full access including user management.',
    [API_ROLES.TRANSIT_AGENCY_ADMIN]: 'Full access to all data and user management features.',
    [API_ROLES.TRANSIT_AGENCY_VIEWER]: 'View-only access to data across the entire agency.',
    [API_ROLES.EMERGENCY_AGENCY_OWNER]: 'Full access including user management.',
    [API_ROLES.EMERGENCY_AGENCY_ADMIN]: 'Full access to all data and user management features.',
    [API_ROLES.EMERGENCY_AGENCY_VIEWER]: 'View-only access to data across the entire agency.',
    [API_ROLES.TRAFFIC_VIEWER]: 'View-only access to data within assigned jurisdictions.',
    [API_ROLES.LYT_ADMIN]: 'Full access to all LYT platform features.',
    [API_ROLES.LYT_EMPLOYEE]: 'Standard LYT employee access.',
};

/**
 * Returns the subtext description for a given role, or null if none defined.
 * @param {string} roleName
 * @returns {string|null}
 */
export const getRoleSubtext = (roleName) => ROLE_SUBTEXTS[roleName] || null;

/**
 * @param {Object} params
 * @param {Array<{ role_id: number, role_name: string }>} params.userRolesList
 * @param {string} params.currentUserRole - Logged-in user's role (API or legacy)
 * @param {string} [params.teamContext] - TEAM_CONTEXT.AGENCY | TEAM_CONTEXT.LYT
 * @param {string} [params.agencyType] - AGENCY_TYPES value for agency-scoped lists
 * @returns {Array<{ role_id: number, role_name: string }>}
 */
export const buildAssignableRolesList = ({
    userRolesList,
    currentUserRole,
    teamContext,
    agencyType = AGENCY_TYPES.TRANSIT,
}) => {
    if (!Array.isArray(userRolesList) || userRolesList.length === 0) {
        return [];
    }

    const resolvedAgencyType = agencyType === AGENCY_TYPES.EMERGENCY
        ? AGENCY_TYPES.EMERGENCY
        : AGENCY_TYPES.TRANSIT;

    let displayOrder = [];
    let allowedRoles = [];

    switch (currentUserRole) {
    case API_ROLES.LYT_ADMIN:
    case LEGACY_ROLES.LYT_ADMIN:
        if (teamContext === TEAM_CONTEXT.LYT) {
            displayOrder = [...LYT_ROLE_DISPLAY_ORDER];
            allowedRoles = pickRoles(userRolesList, displayOrder);
        } else if (teamContext === TEAM_CONTEXT.AGENCY) {
            displayOrder = [...getAgencyRoleDisplayOrder(resolvedAgencyType)];
            allowedRoles = pickRoles(userRolesList, displayOrder);
        } else {
            displayOrder = [...ALL_ASSIGNABLE_ROLE_DISPLAY_ORDER];
            allowedRoles = pickRoles(userRolesList, displayOrder);
        }
        break;

    case API_ROLES.LYT_EMPLOYEE:
    case LEGACY_ROLES.LYT_EMPLOYEE:
        if (teamContext === TEAM_CONTEXT.AGENCY) {
            displayOrder = [...getAgencyRoleDisplayOrder(resolvedAgencyType)];
            allowedRoles = pickRoles(userRolesList, displayOrder);
        } else {
            displayOrder = [API_ROLES.LYT_EMPLOYEE];
            allowedRoles = pickRoles(userRolesList, displayOrder);
        }
        break;

    case API_ROLES.TRANSIT_AGENCY_OWNER:
    case LEGACY_ROLES.AGENCY_OWNER:
    case API_ROLES.TRANSIT_AGENCY_ADMIN:
    case LEGACY_ROLES.AGENCY_ADMIN:
        displayOrder = [...getAgencyOwnerAdminAssignableRoleNames(AGENCY_TYPES.TRANSIT)];
        allowedRoles = pickRoles(userRolesList, displayOrder);
        break;

    case API_ROLES.EMERGENCY_AGENCY_OWNER:
    case API_ROLES.EMERGENCY_AGENCY_ADMIN:
        displayOrder = [...getAgencyOwnerAdminAssignableRoleNames(AGENCY_TYPES.EMERGENCY)];
        allowedRoles = pickRoles(userRolesList, displayOrder);
        break;

    case API_ROLES.TRAFFIC_VIEWER:
        displayOrder = [API_ROLES.TRAFFIC_VIEWER];
        allowedRoles = pickRoles(userRolesList, displayOrder);
        break;

    case API_ROLES.TRANSIT_AGENCY_VIEWER:
    case LEGACY_ROLES.AGENCY_VIEWER:
        displayOrder = [...getAgencyViewerAssignableRoleNames(AGENCY_TYPES.TRANSIT)];
        allowedRoles = pickRoles(userRolesList, displayOrder);
        break;

    case API_ROLES.EMERGENCY_AGENCY_VIEWER:
        displayOrder = [...getAgencyViewerAssignableRoleNames(AGENCY_TYPES.EMERGENCY)];
        allowedRoles = pickRoles(userRolesList, displayOrder);
        break;

    default:
        allowedRoles = [];
    }

    return allowedRoles;
};
